import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useChange, useGetData } from "./hooks";
import { createClient, getClient, updateClient } from "../../../../api/clients";

// Importing Design System components
import { Typography, Button, Input, Select, Card, InfoBox, Radio } from "../../../../design-system";
import Address from "../../../../_components/Address";
import ImportCNPJ from "../../../../_components/ImportCNPJ";

const Client = (props) => {
  const ref = React.useRef(null);
  const params = useParams();
  const [state, handleChange] = useChange({ type: 1, address: {} });
  const [address, setAddress] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  // Campos com erro — usado para mostrar borda vermelha via prop `error` do Input
  const [fieldErrors, setFieldErrors] = React.useState(new Set());

  // Limpa o erro do campo ao editar
  const handleChangeWithClear = (e) => {
    const { name } = e.target;
    if (fieldErrors.has(name)) {
      setFieldErrors((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
    handleChange(e);
  };

  const err = (name) => (fieldErrors.has(name) ? " " : undefined);

  const onSave = async () => {
    const { data: payload, errors } = useGetData(state);

    if (errors.size > 0) {
      setFieldErrors(errors);

      // Scroll suave até o primeiro campo inválido (ordem do formulário)
      const fieldOrder = state.type == 1
        ? ["cpf", "name", "email", "phone_number"]
        : state.type == 2
        ? ["cnpj", "company_name", "company_email", "company_phone_number", "fancy_name", "company_state_registry"]
        : ["cpf", "name", "email", "phone_number", "state_registry"];

      setTimeout(() => {
        for (const name of fieldOrder) {
          if (errors.has(name)) {
            const el = document.querySelector(`[name="${name}"]`);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              el.focus({ preventScroll: true });
            }
            break;
          }
        }
      }, 50);

      return;
    }

    // Valida campos de endereço
    const addressErrors = new Set();
    if (!address.postal_code) addressErrors.add("postal_code");
    if (!address.state_id) addressErrors.add("state_id");
    if (!address.city_id) addressErrors.add("city_id");
    if (!address.district) addressErrors.add("district");
    if (!address.street) addressErrors.add("street");

    if (addressErrors.size > 0) {
      toast.error("Preencha os campos de endereço corretamente", {
        position: "top-center",
        autoClose: 5000,
        toastId: "error-create-cliente-without-address",
      });
      return;
    }

    payload["address"] = {
      ...address,
      state_location: address.state_id,
      city_location: address.city_id,
    };

    // Clean up temporary UI keys
    delete payload["address"].state_id;
    delete payload["address"].city_id;
    delete payload["address"].state;
    delete payload["address"].state_uf;
    delete payload["address"].city;

    const request = params.id
      ? updateClient(params.id, payload)
      : createClient(payload);

    setLoading(true);
    request
      .then((_) => {
        // Dispara evento GTM/GA4 ao criar novo cliente (Story #1559)
        if (!params.id && typeof window !== "undefined" && window.dataLayer) {
          window.dataLayer.push({
            event: "novo_cadastro",
            tipo: state.type == 2 ? "pessoa_juridica" : state.type == 3 ? "produtor_rural" : "pessoa_fisica",
          });
        }

        toast.success("Cliente salvo", {
          position: "top-center",
          autoClose: 5000,
          toastId: "success-create-cliente",
        });
        if (props.onSave) {
          props.onSave(_);
        }

        if (props.closeIt) {
          setTimeout(() => {
            props.closeIt();
          }, 3000);
        } else {
          setTimeout(() => {
            window.location.href = "/clients";
          }, 3000);
        }
      })
      .catch((error) => {
        if (error.non_field_errors) {
          if (error.non_field_errors[0] === "CPF Already registred") {
            toast.error("CPF Já está cadastrado", {
              position: "top-center",
              toastId: "error-CPF-Already-registred",
            });
            return;
          } else if (error.non_field_errors[0] === "CNPJ Already registred") {
            toast.error("CNPJ Já está cadastrado", {
              position: "top-center",
              toastId: "error-CNPJ-Already-registred",
            });
          }
        }

        // Feedback visual para erros de campo retornados pelo backend
        const backendFieldErrors = new Set();
        const fieldMap = {
          email: state.type == 2 ? "company_email" : "email",
          phone_number: state.type == 2 ? "company_phone_number" : "phone_number",
          name: state.type == 2 ? "company_name" : "name",
        };
        for (const [backendField, frontendField] of Object.entries(fieldMap)) {
          if (error[backendField]) {
            backendFieldErrors.add(frontendField);
            toast.error(
              Array.isArray(error[backendField])
                ? error[backendField][0]
                : String(error[backendField]),
              { position: "top-center", toastId: `error-field-${backendField}` }
            );
          }
        }
        if (backendFieldErrors.size > 0) {
          setFieldErrors((prev) => new Set([...prev, ...backendFieldErrors]));
        } else if (!error.non_field_errors) {
          toast.error("Erro ao salvar cliente. Verifique os dados e tente novamente.", {
            position: "top-center",
            toastId: "error-save-client-generic",
          });
        }
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 3000);
      });
  };


  const onChangeAddress = (addr) => {
    setAddress(addr);
  };

  React.useEffect(() => {
    if (params.id) {
      setLoading(true);
      getClient(params.id)
        .then((resp) => {
          // Sync address to state so our controlled component renders it
          if (resp.address) {
            setAddress({
              ...resp.address,
              state_id: resp.address.state_id || resp.address.state_location,
              city_id: resp.address.city_id || resp.address.city_location,
            });
          }

          if (resp.company) {
            handleChange.forceUpdate({
              type: 2,
              company_name: resp.name,
              company_email: resp.email,
              company_phone_number: resp.phone_number,
              fancy_name: resp.company.fancy_name,
              cnpj: resp.company.cnpj,
              company_state_registry: resp.company.state_registry,
              address: {},
            });
          } else {
            handleChange.forceUpdate({
              type: resp.individual.state_registry ? 3 : 1,
              name: resp.name,
              email: resp.email,
              phone_number: resp.phone_number,
              cpf: resp.individual.cpf,
              address: {},
              state_registry: resp.individual.state_registry
                ? resp.individual.state_registry
                : "",
            });
          }
        })
        .finally(() => {
          setTimeout(() => {
            setLoading(!true);
          }, 2000);
        });
    }
  }, [params.id]);

  return (
    <div className="container-fluid py-4" ref={ref}>
      
      {/* Dados do cliente */}
      <div className="row mb-4">
        <div className="col-12">
          <Typography variant="h5" style={{ color: '#444', fontWeight: 600 }}>
            Dados do cliente
          </Typography>
        </div>
      </div>

      {/* Seleção do Tipo de Cliente */}
      <div className="row mb-4">
        <div className="col-12">
          <Typography variant="small" style={{ color: '#444', display: 'block', marginBottom: '12px', fontWeight: 600, fontSize: '15px' }}>
            {!!params.id ? "Tipo de cliente" : "Selecione o tipo"}
          </Typography>
          {!!params.id ? (
            <div style={{ maxWidth: '300px' }}>
              <InfoBox
                icon={
                  state.type == 1 ? "fa fa-user" :
                  state.type == 2 ? "fa fa-building" :
                  "fa fa-leaf"
                }
              >
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#444' }}>
                  {state.type == 1 ? "Pessoa Física" :
                   state.type == 2 ? "Pessoa Jurídica" :
                   "Produtor Rural"}
                </span>
              </InfoBox>
            </div>
          ) : (
            <div className="d-flex flex-wrap" style={{ gap: '24px' }}>
              <Radio
                label="Pessoa Física"
                name="type"
                value={1}
                checked={state.type == 1}
                onChange={handleChange}
                disabled={loading}
              />
              <Radio
                label="Pessoa Jurídica"
                name="type"
                value={2}
                checked={state.type == 2}
                onChange={handleChange}
                disabled={loading}
              />
              <Radio
                label="Produtor Rural"
                name="type"
                value={3}
                checked={state.type == 3}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          )}
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6 mb-3">
          {state.type == 2 ? (
            <ImportCNPJ 
              label="CNPJ"
              name="cnpj"
              cnpj={state.cnpj} 
              value={state.cnpj || ""}
              onChange={handleChangeWithClear}
              disabled={loading}
              type="person" 
              onSuccess={(data) => {
                const newState = { ...state };
                if (data.name) newState.company_name = data.name;
                if (data.email) newState.company_email = data.email;
                if (data.phone_number) newState.company_phone_number = data.phone_number;
                if (data.company && data.company.fancy_name) newState.fancy_name = data.company.fancy_name;

                if (data.address) {
                  const mappedAddress = {
                    postal_code: data.address.postal_code || "",
                    street: data.address.street || "",
                    street_number: data.address.street_number || "",
                    complement: data.address.complement || "",
                    district: data.address.district || "",
                    state_id: data.address.state_location || null,
                    state_uf: data.address.state_location_uf || null,
                    city_id: data.address.city_location || null,
                    city: data.address.city_location_name || "",
                  };
                  // Bug 1806: sincroniza o componente Address (estado separado)
                  setAddress(mappedAddress);
                  newState.address = mappedAddress;
                }
                handleChange.forceUpdate(newState);
              }}
            />
          ) : (
            <Input
              label="CPF"
              disabled={loading}
              onChange={handleChangeWithClear}
              type="cpf"
              name="cpf"
              value={state.cpf || ""}
              placeholder="Insira aqui o CPF"
              error={err("cpf")}
            />
          )}
        </div>
        <div className="col-md-6 mb-3">
          <Input
            disabled={loading}
            maxLength={60}
            label={state.type == 2 ? "Razão Social" : "Nome"}
            placeholder={state.type == 2 ? "Insira aqui a Razão Social" : "Insira aqui o Nome"}
            onChange={handleChangeWithClear}
            name={state.type == 2 ? "company_name" : "name"}
            value={(state.type == 2 ? state.company_name : state.name) || ""}
            type="text"
            error={err(state.type == 2 ? "company_name" : "name")}
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-6 mb-3">
          <Input
            label="Email"
            placeholder="Insira aqui o email"
            disabled={loading}
            maxLength={100}
            onChange={handleChangeWithClear}
            name={state.type == 2 ? "company_email" : "email"}
            type="email"
            value={(state.type == 2 ? state.company_email : state.email) || ""}
            error={err(state.type == 2 ? "company_email" : "email")}
          />
        </div>
        <div className="col-md-6 mb-3">
          <Input
            label="Telefone"
            placeholder="Insira aqui o telefone"
            disabled={loading}
            onChange={handleChangeWithClear}
            name={state.type == 2 ? "company_phone_number" : "phone_number"}
            type="telefone"
            value={(state.type == 2 ? state.company_phone_number : state.phone_number) || ""}
            error={err(state.type == 2 ? "company_phone_number" : "phone_number")}
          />
        </div>
      </div>

      {(state.type == 2 || state.type == 3) && (
        <div className="row mb-3">
          {state.type == 2 && (
            <div className="col-md-6 mb-3">
              <Input
                label="Nome Fantasia"
                placeholder="Insira aqui o Nome Fantasia"
                disabled={loading}
                onChange={handleChangeWithClear}
                type="text"
                value={state.fancy_name || ""}
                name="fancy_name"
                error={err("fancy_name")}
              />
            </div>
          )}
          <div className="col-md-6 mb-3">
            <Input
              label="Inscrição Estadual (IE)"
              disabled={loading}
              onChange={handleChangeWithClear}
              type="text"
              maxLength={20}
              value={(state.type == 2 ? state.company_state_registry : state.state_registry) || ""}
              name={state.type == 2 ? "company_state_registry" : "state_registry"}
              placeholder="Informe aqui"
              error={err(state.type == 2 ? "company_state_registry" : "state_registry")}
            />
            <div className="mt-2 d-flex flex-column align-items-start">
              <Button
                variant="primary"
                className="mb-2"
                onClick={() => {
                  const name = state.type == 2 ? "company_state_registry" : "state_registry";
                  handleChangeWithClear({ target: { name, value: "ISENTO" } });
                }}
              >
                Isento de Inscrição Estadual
              </Button>
              <InfoBox
                onClick={() => {
                  window.open("https://dfe-portal.svrs.rs.gov.br/NFE/CCC", "__blank");
                }}
              >
                Não sei a Inscrição Estadual
              </InfoBox>
            </div>
          </div>
        </div>
      )}

      {/* Endereço do cliente */}
      <div className="row mt-5 mb-4">
        <div className="col-12">
          <Typography variant="h5" style={{ color: '#444', fontWeight: 600 }}>
            Endereço do cliente
          </Typography>
        </div>
      </div>
      
      <div className="address-container">
        <Address 
          data={address} 
          disabled={loading} 
          onChange={onChangeAddress} 
        />
      </div>

      {/* Action Buttons at Bottom */}
      <div className="row mt-5 pt-4 border-top">
        <div className="col-12 d-flex justify-content-end align-items-center" style={{ gap: '16px' }}>
          <Button
            disabled={loading}
            variant="tertiary"
            onClick={() => {
              if (props.closeIt) {
                props.closeIt();
              } else {
                window.location.href = "/clients";
              }
            }}
          >
            Cancelar
          </Button>
          <Button 
            disabled={loading} 
            onClick={onSave}
            variant="primary"
          >
            Salvar
          </Button>
        </div>
      </div>

    </div>
  );
};
export { Client as CreateClientContainer };
export default Client;



// WEBPACK FOOTER //
// ./src/views/AppViews/Clients/Client/index.jsx